import {
  IAppData,
  IPlatformAPI,
  IPlatformServices,
  IWixAPI,
  IWidgetControllerConfig,
} from '@wix/native-components-infra/dist/src/types/types';
import { SiteStore } from '@wix/wixstores-client-storefront-sdk';
import { getReleaseFromBaseUrl } from '@wix/native-components-infra';
import {
  IControllerFactories,
  ICreateViewerScriptOptions,
  CreatedViewerScript,
  IAppParams,
  EcomPlatformViewerScriptContext,
} from '../types';
import { EcomPlatformWidgetID, VIEWER_SCRIPT_DSN } from '../../constants';
import {
  createControllerRaven,
  createFakeRaven,
  setSentryInstance,
  withErrorReportingWrapping,
} from '../../error-reporter/errorReporter';
import { shouldOpenMiniCart, getTinyCartParams } from '../viewerScript.utils';
import { initController } from './initController';
import { getControllerFactory } from './getControllerFactory';
import { CartService } from '../../services/cart-service/CartService';
import { CurrentCartService } from '../../services/current-cart-service/CurrentCartService';
import { SecondaryCurrentCartService } from '../../services/secondary-current-cart-service/SecondaryCurrentCartService';
import { EcommerceSettingsService } from '../../services/ecommerce-settings-service/EcommerceSettingsService';
import { PurchaseRecommendationsService } from '../../services/purchase-recommendations-service/PurchaseRecommendationsService';
import { createPublicApiExports } from '../../publicApi/createPublicApiExports';

const defaultOptions: ICreateViewerScriptOptions = {
  useWorkerRaven: true,
};

export function createViewerScript(
  controllerFactories?: IControllerFactories,
  options: Partial<ICreateViewerScriptOptions> = {},
): CreatedViewerScript {
  const context: EcomPlatformViewerScriptContext = {
    controllerConfigs: [],
  } as unknown as EcomPlatformViewerScriptContext;

  options = { ...defaultOptions, ...options };

  return withErrorReportingWrapping({
    initAppForPage: (
      initParams: IAppData,
      apis: IPlatformAPI,
      namespaces: IWixAPI,
      platformServices: IPlatformServices,
    ): Promise<EcomPlatformViewerScriptContext> => {
      const userMonitoringContext = {
        id: initParams.instanceId,
        url: namespaces.location.baseUrl,
        uuid: platformServices.bi.visitorId,
      };

      context.siteStore = new SiteStore(
        initParams as IAppParams,
        apis,
        namespaces,
        platformServices,
      );
      context.wixApi = namespaces;
      context.platformServices = platformServices;
      context.platformAPIs = apis;

      if (options.useWorkerRaven) {
        const sentryInstance = platformServices.monitoring.createMonitor(
          VIEWER_SCRIPT_DSN,
          (data) => {
            data.environment = 'Worker';
            data.release =
              initParams.url &&
              getReleaseFromBaseUrl(initParams.url, {
                artifactName: true,
              });
            return data;
          },
        );
        sentryInstance.setUserContext(userMonitoringContext);
        setSentryInstance(sentryInstance);
        context.createMonitoring = createControllerRaven(
          platformServices,
          userMonitoringContext,
          context.siteStore,
        );
      } else {
        context.createMonitoring = createFakeRaven();
      }

      const isLightbox =
        context.siteStore.siteApis.currentPage?.type === 'lightbox';
      context.eCommerceSettingsService = new EcommerceSettingsService({
        context,
        apis,
        platformServices,
        isLightbox,
      });
      context.currentCartService =
        isLightbox || (options.isInIframe && context.siteStore.isPreviewMode())
          ? new SecondaryCurrentCartService({ context, apis })
          : new CurrentCartService({ context, apis });
      context.cartService = new CartService({
        context,
      });
      context.purchaseRecommendationsService =
        new PurchaseRecommendationsService({ platformServices });
      if (!context.siteStore.isSSR()) {
        context.currentCartService.init();
        context.eCommerceSettingsService!.init();
      }

      return Promise.resolve(context);
    },
    createControllers: (
      controllerConfigs: IWidgetControllerConfig[],
      controllerInstances: any,
    ) => {
      context.controllerConfigs = controllerConfigs;
      if (shouldOpenMiniCart(context)) {
        context.tinyCartParams = getTinyCartParams(controllerConfigs);
      }

      if (
        controllerConfigs.some((c) => c.type === EcomPlatformWidgetID.CART_ICON)
      ) {
        context.siteStore.isCartIconExists = true;
      }

      return controllerConfigs.map((props) => {
        const ctrlFactory = getControllerFactory(
          controllerInstances,
          controllerFactories,
          props.type,
        );
        const ctrl = initController(ctrlFactory, context, props);
        return Promise.resolve(ctrl);
      });
    },
    exports: createPublicApiExports({
      context,
      origin: 'wixcode',
    }),
  });
}
